import React from "react";
import type { SectionComponentProps } from "../../registry.js";

interface StorySplitProps {
  eyebrow?: string;
  headline?: string;
  body?: string;
  imageUrl?: string;
  stat?: string;
  statLabel?: string;
  reverse?: boolean;
}

export function StorySplit({ props }: SectionComponentProps) {
  const {
    eyebrow = "Our Story",
    headline = "Crafted with passion, served with care",
    body = "Every dish tells a story — sourced from local farms, prepared by hand, and plated with intention. We believe great food starts long before it reaches the table.",
    imageUrl,
    stat = "12+",
    statLabel = "Years serving the neighborhood",
    reverse = false,
  } = props as StorySplitProps;

  const textCol = (
    <div className="flex flex-col justify-center gap-6 px-8 py-16 md:px-16">
      {eyebrow && (
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">
          {eyebrow}
        </span>
      )}
      <h2 className="text-3xl md:text-4xl font-bold text-foreground leading-snug">{headline}</h2>
      <p className="text-muted-foreground leading-relaxed max-w-prose">{body}</p>
      {stat && (
        <div className="flex items-baseline gap-3 pt-2">
          <span className="text-5xl font-bold text-primary tabular-nums">{stat}</span>
          <span className="text-sm text-muted-foreground max-w-[12rem] leading-snug">{statLabel}</span>
        </div>
      )}
    </div>
  );

  const imageCol = (
    <div className="relative min-h-72 md:min-h-0 overflow-hidden">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-accent/15 to-muted">
          <div className="absolute bottom-8 right-8 w-32 h-32 rounded-full bg-primary/10 blur-2xl" />
          <div className="absolute top-12 left-12 w-20 h-20 rounded-full bg-accent/20 blur-xl" />
        </div>
      )}
    </div>
  );

  return (
    <section className={`grid md:grid-cols-2 min-h-[55vh] bg-background`} id="story">
      {reverse ? <>{imageCol}{textCol}</> : <>{textCol}{imageCol}</>}
    </section>
  );
}
